// src/features/admin/logic.ts
// Pure helpers shared across the admin feature — no React imports.

import type { PaginatedRequest } from '@core/types/pagination';

/** Build a well-formed PaginatedRequest — sort/view are required fields on this type. */
export function paginationParams(limit = 50): PaginatedRequest {
  return { pagination: { cursor: '', limit, direction: 'forward' }, sort: [], view: 0 };
}

export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export function fmtDateTime(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(locale, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export function bytesToHuman(bytes: number): string {
  if (!bytes) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export function minorToDisplay(minorUnits: number): string {
  return (minorUnits / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Strip an ENUM_PREFIX_ off a proto enum value and lowercase it for i18n keys. */
export function enumKey(value: string | undefined, prefix: string): string {
  if (!value) return 'unspecified';
  return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

export const AUDIT_SCOPE_VALUES = ['AUDIT_SCOPE_PLATFORM', 'AUDIT_SCOPE_TENANT', 'AUDIT_SCOPE_TEAM', 'AUDIT_SCOPE_PROJECT', 'AUDIT_SCOPE_USER'] as const;

export const AUDIT_ACTION_VALUES = [
  'AUDIT_ACTION_CREATE', 'AUDIT_ACTION_READ', 'AUDIT_ACTION_UPDATE', 'AUDIT_ACTION_DELETE',
  'AUDIT_ACTION_LOGIN', 'AUDIT_ACTION_LOGOUT', 'AUDIT_ACTION_EXPORT', 'AUDIT_ACTION_SHARE',
  'AUDIT_ACTION_APPROVE', 'AUDIT_ACTION_REJECT', 'AUDIT_ACTION_ESCALATE',
] as const;
